
import React, {
  createContext, useCallback, useContext, useMemo, type ReactNode } from 'react';
export const keyify = (key) => key.map((item) => JSON.stringify(item)).join('-');

const CacheContext = createContext<any>(null);

export const useCache = () => useContext(CacheContext);
interface DataCacheProviderProps {
  children: ReactNode;
}

export const DataCacheProvider = ({ children }: DataCacheProviderProps) => {
  const map = useMemo(() => new Map(), []);

  const getCache = useCallback((_key) => {
    const key = keyify(_key);

    const cacheValue = map.get(key);

    if (!cacheValue) return undefined;

    if (new Date().getTime() > cacheValue.expiry.getTime()) {
      map.delete(key);
      return undefined;
    }

    return cacheValue.data;
  }, [map]);

  const setCache = useCallback((_key, value, ttl = 30) => {
    const key = keyify(_key);

    const time = new Date();
    time.setSeconds(time.getSeconds() + ttl);

    map.set(key, { expiry: time, data: value });
  }, [map]);

  const clearCache = useCallback(() => {
    map.clear();
  }, [map]);

  const deleteCache = useCallback((_key) => {
    const key = keyify(_key);

    map.delete(key);
  }, [map]);

  const value = useMemo(() => ({
    getCache,
    setCache,
    clearCache,
    deleteCache,
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [map]);

  return (
    <CacheContext.Provider value={value}>
      {children}
    </CacheContext.Provider>
  );
};
